import React from 'react';
import './ModernDashboard.css';

const ModernDashboard = ({ 
  children, 
  title, 
  onCreateNote, 
  onToggleFilters, 
  filtersVisible, 
  showUpgradeMessage, 
  showUpgradeLimitMessage, 
  onUpgradeClick,
  userPlan,
  noteCount,
  filters,
  onFilterChange,
  onFilterSelect,
  selectedFilter,
  colorFilter,
  colorOptions,
  clearFilters,
  currentNotesCount,
  totalNotesCount,
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  notesPerPage = 50,
  onPageSizeChange,
  onFetchTotalCount,
  allNotesLoaded = false,
  selectionMode = false,
  selectedCount = 0,
  canConvertNotes = true,
  onSelectionStart,
  onConvertSelected,
  onDeleteSelected,
  onClearSelection,
  onClearAll,
  onStartTutorial
}) => {
  // Check if user can create more notes
  const canCreateNote = userPlan !== 'Basic' || noteCount < 30;
  
  return (
    <div className="modern-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">{title}</h1>
          </div>
          
          <div className="header-actions">
            {/* Utility Buttons Group */}
            <div className="header-actions-utility">
              {typeof onStartTutorial === 'function' && (
                <button
                  className="modern-icon-button tutorial-btn-header"
                  onClick={onStartTutorial}
                  title="Start Tutorial - Learn how to use Ideal Scribe"
                >
                  <i className="fas fa-question-circle"></i>
                  <span>Start Here</span>
                </button>
              )}
              <button
                className={`filter-toggle-btn modern-icon-button ${filtersVisible ? 'active' : ''}`}
                onClick={onToggleFilters}
                title="Toggle Filters"
              >
                <i className="fas fa-filter"></i>
                <span>Filters</span>
              </button>
              
              {typeof onClearAll === 'function' && (
                <button
                  className="modern-icon-button clear-selection-btn"
                  onClick={onClearAll}
                  title="Clear filters and selections, refresh list"
                >
                  <i className="fas fa-times"></i>
                  <span>Clear</span>
                </button>
              )}

              {typeof onDeleteSelected === 'function' && canConvertNotes && (
                <button
                  className="modern-icon-button delete-selected-btn"
                  onClick={onDeleteSelected}
                  title={selectedCount === 0 ? 'Click to select notes to delete' : `Delete ${selectedCount} selected note${selectedCount > 1 ? 's' : ''}`}
                >
                  <i className="fas fa-trash"></i>
                  <span>
                    Delete
                    {selectedCount > 0 ? ` (${selectedCount})` : ''}
                  </span>
                </button>
              )}
            </div>

            {/* Visual Separator */}
            <div className="button-separator"></div>

            {/* Primary Action Buttons Group */}
            <div className="header-actions-primary">
              {canCreateNote && (
                <button
                  className="create-note-btn modern-icon-button"
                  onClick={onCreateNote}
                  title="Create New Note"
                >
                  <i className="fas fa-plus"></i>
                  <span>New Note</span>
                </button>
              )}
              
              {typeof onConvertSelected === 'function' && canConvertNotes && (
                <button
                  className="modern-icon-button convert-packit-btn"
                  onClick={onConvertSelected}
                  title={selectedCount === 0 ? 'Click to select notes to convert' : 'Convert selected notes to a Pack-It'}
                >
                  <i className="fas fa-suitcase-rolling"></i>
                  <span>
                    Convert to Pack-It
                    {selectedCount > 0 ? ` (${selectedCount})` : ''}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Messages */}
      {showUpgradeMessage && (
        <div className="upgrade-banner warning">
          <div className="upgrade-content">
            <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', color: 'var(--warning-600)' }}></i>
            <div className="upgrade-text">
              <h4>Approaching Free Tier Limit</h4>
              <p>You are nearing the free tier limit of 30 notes. Upgrade your plan to see more!</p>
            </div>
            <button className="upgrade-btn" onClick={onUpgradeClick}>
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      {showUpgradeLimitMessage && (
        <div className="upgrade-banner error">
          <div className="upgrade-content">
            <i className="fas fa-times-circle" style={{ fontSize: '24px', color: 'var(--error-600)' }}></i>
            <div className="upgrade-text">
              <h4>Free Tier Limit Reached</h4>
              <p>You are viewing the last 30 notes. Upgrade your plan to see more!</p>
            </div>
            <button className="upgrade-btn" onClick={onUpgradeClick}>
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      {/* Filters Section */}
      {filtersVisible && (
        <div className="filters-section">
          <div className="filters-header">
            <h3>Filter Notes</h3>
          </div>
          
          <div className="filters-grid">
            <div className="filter-group">
              <label className="filter-label">Filter Type</label>
              <select 
                className="filter-select" 
                onChange={(e) => onFilterSelect(e.target.value)}
                value={selectedFilter}
              >
                <option value="">Select Filter</option>
                <option value="emailSubject">Email Subject or Note Name</option>
                <option value="bodyText">Note Text</option>
                <option value="categoryColor">Color</option>
                <option value="favorite">Favorite</option>
                <option value="noteCreationDate">Note Creation Date</option>
                <option value="lastEdited">Last Edited</option>
                <option value="fromEmail">From Email</option>
                <option value="completed">Completed</option>
                <option value="name">Recommended Name</option>
                <option value="description">Recommended Description</option>
              </select>
            </div>

            {selectedFilter && selectedFilter !== 'favorite' && selectedFilter !== 'completed' && (
              <div className="filter-group">
                <label className="filter-label">Filter Value</label>
                {selectedFilter === 'categoryColor' ? (
                  <select
                    name="categoryColor"
                    className="filter-select"
                    value={colorFilter}
                    onChange={onFilterChange}
                  >
                    <option value="">Select Color</option>
                    {colorOptions.map(color => (
                      <option key={color} value={color.toLowerCase()}>{color}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    name={selectedFilter}
                    className="filter-input"
                    placeholder={
                      selectedFilter === "noteCreationDate" || selectedFilter === "lastEdited"
                        ? "yyyy-mm-dd"
                        : `Filter by ${selectedFilter}`
                    }
                    onChange={onFilterChange}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="dashboard-content">
        {children}
      </div>

      {/* Pagination */}
      <div className="pagination-section">
          <div className="pagination-info">
            <div className="pagination-stats" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
              <span className="stat-item">
                <span className="stat-number">{currentNotesCount}</span>
                <span className="stat-label">Showing</span>
              </span>
              <span className="stat-divider">•</span>
              <span className="stat-item">
                <span className="stat-number">{totalNotesCount}</span>
                <span className="stat-label">
                  Total
                  {!allNotesLoaded && onFetchTotalCount && (
                    <button
                      onClick={onFetchTotalCount}
                      className="refresh-count-btn"
                      title="Get accurate total count"
                      style={{
                        marginLeft: '8px',
                        padding: '2px 6px',
                        fontSize: '12px',
                        background: 'transparent',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        color: 'var(--text-secondary)'
                      }}
                    >
                      <i className="fas fa-sync-alt"></i>
                    </button>
                  )}
                </span>
              </span>
              <span className="stat-divider">•</span>
              <span className="stat-item">
                <span className="stat-number">{currentPage}</span>
                <span className="stat-label">Page {currentPage} of {totalPages}</span>
              </span>
              {onPageSizeChange && (
                <>
                  <span className="stat-divider">•</span>
                  <span className="stat-item">
                    <label className="stat-label" style={{ marginRight: '8px' }}>Per Page:</label>
                    <select
                      value={notesPerPage}
                      onChange={(e) => onPageSizeChange(Number(e.target.value))}
                      disabled={isLoading}
                      style={{
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid var(--border-primary)',
                        borderRadius: '4px',
                        background: 'var(--bg-elevated)',
                        color: 'var(--text-primary)',
                        cursor: isLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={0}>All</option>
                    </select>
                  </span>
                </>
              )}
            </div>
          </div>
          
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageNumber = index + 1;
                  const isActive = pageNumber === currentPage;
                  const isNearActive = Math.abs(pageNumber - currentPage) <= 2;
                  
                  if (isActive || isNearActive || pageNumber === 1 || pageNumber === totalPages) {
                    return (
                      <button
                        key={pageNumber}
                        className={`page-btn ${isActive ? 'active' : ''}`}
                        onClick={() => onPageChange(pageNumber)}
                        disabled={isLoading}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                    return <span key={pageNumber} className="page-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
    </div>
  );
};

export default ModernDashboard;
